import dayjs from 'dayjs'

import { exportDataToExcel, getColumnInfo } from '../helpers'
import type {
  AccountingReport,
  SalaryDeductionDeviceReport,
  SalaryDeductionUsageReport,
} from '../resources'
import adminResources from '../resources'
import type { ExportFiltersType } from '../resources/reports/types'

import type { FilterOptionsType } from './types'
import { downloadTextFile, groupAndSumByKeys } from './utils'

const DATE_FORMAT = 'DD.MM.YYYY'

// format the number with 2 decimals and dot decimal separator
function formatNumber(num: number | string) {
  if (typeof num === 'number') {
    return num.toFixed(2).replace(',', '.')
  }
  return parseFloat(num).toFixed(2).replace(',', '.')
}

function formatVismaPayrollNumber(num: number, isVismaPayroll: boolean) {
  return isVismaPayroll
    ? num.toFixed(2)
    : num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export function generateSapV2Data(data: (SalaryDeductionUsageReport | AccountingReport)[]) {
  const preparedData: {
    SHKZG: string
    HKONT: string
    WRBTR: number | string
    KOSTL: string
    MWSKZ: string
    SGTXT: string
  }[] = []

  const filteredData = groupAndSumByKeys(data, ['accountingCode'], ['netVat', 'netNoVat'])
  filteredData.forEach(item => {
    if (item.netVat !== 0) {
      preparedData.push({
        SHKZG: 'S',
        HKONT: '680500',
        WRBTR: item.netVat,
        KOSTL: item.departmentCode,
        MWSKZ: 'l3',
        SGTXT: '',
      })
    }
    if (item.netNoVat !== 0) {
      preparedData.push({
        SHKZG: 'S',
        HKONT: '680500',
        WRBTR: item.netNoVat,
        KOSTL: item.departmentCode,
        MWSKZ: 'l0',
        SGTXT: '',
      })
    }
  })
  return preparedData
}

export function generateSapV2Device(data: SalaryDeductionDeviceReport[]) {
  const preparedData: {
    SHKZG: string
    HKONT: string
    WRBTR: number | string
    KOSTL: string
    MWSKZ: string
    SGTXT: string
  }[] = []
  data.forEach(item => {
    preparedData.push({
      SHKZG: 'S',
      HKONT: '680500',
      WRBTR: item.amount,
      KOSTL: item.departmentCode,
      MWSKZ: 'l3',
      SGTXT: '',
    })
  })
  return preparedData
}

// grossDebit, grossCredit
export function vismaGlobalAccountingFormat(filters: Partial<ExportFiltersType>, data: any[]) {
  if (filters.isGlobal) {
    return getReportExportUsingAPI({
      reportName: 'accounting',
      formatName: 'visma_global',
      ...filters,
    })
  }

  let text = ''
  const filteredData = groupAndSumByKeys(
    data,
    ['invoiceNo', 'departmentCode', 'accountingCode'],
    ['grossDebit', 'grossCredit']
  )
  filteredData.forEach(item => {
    const debitAmount = parseFloat(item.grossDebit ?? '0.00')
    const creditAmount = parseFloat(item.grossCredit ?? '0.00')

    if (debitAmount !== 0) {
      text += `1,${dayjs(item.fromPeriod).format(DATE_FORMAT)},${item.invoiceNo},${dayjs(
        item.toPeriod
      ).format(DATE_FORMAT)},${item.accountingCode ?? ''},"",${
        item.departmentCode ?? ''
      },0,6915,0,0,0,0,0,${formatNumber(debitAmount)},0,"",0,0,"",0,0,"",0,0,0,0,0,0,0\n`
    }

    if (creditAmount !== 0) {
      text += `1,${dayjs(item.fromPeriod).format(DATE_FORMAT)},${item.invoiceNo},${dayjs(
        item.toPeriod
      ).format(DATE_FORMAT)},${item.accountingCode ?? ''},"",${
        item.departmentCode ?? ''
      },0,0,1575,0,0,0,0,${formatNumber(Math.abs(creditAmount))},0,"",0,0,"",0,0,"",0,0,0,0,0,0,0\n`
    }
  })
  downloadTextFile(text, 'accounting-visma-global.txt')
}

export function ssdTripletexFormat(
  data: SalaryDeductionDeviceReport[],

  options?: { columnFormats?: { [key: string]: string }; currency: string | undefined }
) {
  const preparedData: {}[] = []
  data.forEach(item => {
    preparedData.push({
      year: dayjs(item.orderDate).year(),
      month: dayjs(item.orderDate).month() + 1,
      employeeNo: item.employeeNo,
      accountingCode: item.accountingCode,
      comment: '',
      quantity: 1,
      amount: item.amount,
      project: item.projectDevice,
      department: item.departmentCode,
      currency: options?.currency,
    })
  })
  const headers = getColumnInfo(Object.keys(preparedData[0]))
  exportDataToExcel(preparedData, 'salary-deduction-device-tripletex', {
    headers,
    columnFormats: options?.columnFormats,
  })
}

export function ssuTripletexFormat(
  data: SalaryDeductionUsageReport[],
  columnFormats?: { [key: string]: string }
) {
  const preparedData: {}[] = []
  data.forEach(item => {
    preparedData.push({
      year: dayjs(item.invoiceDate).year(),
      month: dayjs(item.invoiceDate).month() + 1,
      employeeNo: item.employeeNo,
      accountingCode: item.accountingCode,
      comment: '',
      quantity: item.posts,
      amount: item.amount,
      project: item.projectUsage,
      department: item.departmentCode,
      currency: item.currency,
    })
  })
  const headers = getColumnInfo(Object.keys(preparedData[0]))
  exportDataToExcel(preparedData, 'salary-deduction-usage-tripletex', { headers, columnFormats })
}

export function ssdHuldtAndLillevik(data: SalaryDeductionDeviceReport[]) {
  function generateExportFormat(data: SalaryDeductionDeviceReport[]) {
    let exportText = ''

    data.forEach(item => {
      const employeeNo = item.employeeNo.padStart(6, '0')
      const accountingCode = item.accountingCode.padStart(5, '0')
      const department = (item.departmentName || item.departmentCode).padEnd(12, ' ')
      const projectDevice = item.projectDevice.padEnd(12, ' ')
      const date = item.deliveryDate ? dayjs(item.deliveryDate).format('DDMMYY') : ''
      const amount = parseFloat(String(item.amount)).toFixed(2) ?? ''

      const line =
        `${employeeNo}${accountingCode}${department}${projectDevice}` +
        ' '.repeat(60) +
        `${date}100` +
        ' '.repeat(10) +
        `10${amount}` +
        ' '.repeat(30)
      exportText += line + '\n'
    })

    return exportText
  }
  // Call the generateExportFormat function with your 'data' array
  const exportData = generateExportFormat(data)

  // Determine the extension based on customer head id
  let extension = '.hlw' // Default
  if (data.length > 0) {
    const customerHeadId = data[0].customerHeadId
    if (customerHeadId === 1000178) {
      extension = '.HLW'
    } else if (customerHeadId === 1000180 || customerHeadId === 1000218) {
      extension = '.txt'
    }
  }
  downloadTextFile(exportData, `salary-deduction-device-huldt-og-lillevik${extension}`)
}

export function ssuHuldtAndLillevik(data: SalaryDeductionUsageReport[]) {
  // Function to generate the export format
  function generateExportFormat(data: SalaryDeductionUsageReport[]) {
    let exportText = ''

    data.forEach(item => {
      const employeeNo = item.employeeNo.padStart(6, '0')
      const accountingCode = item.accountingCode.padStart(5, '0')
      const department = (item.departmentName || item.departmentCode).padEnd(12, ' ')
      const projectUsage = item.projectUsage.padEnd(12, ' ')
      const date = item.deliveryDate ? dayjs(item.deliveryDate).format('DDMMYY') : ''
      const amount =
        (
          parseFloat(item.amount ? String(item.amount) : '0.0') +
          parseFloat(item.vat ? String(item.vat) : '0.0')
        ).toFixed(2) ?? ''

      if (item.employeeNo && item.netVat) {
        const line =
          `${employeeNo}${accountingCode}${department}${projectUsage}` +
          ' '.repeat(60) +
          `${date}100` +
          `10${amount}` +
          ' '.repeat(30)
        exportText += line + '\n'
      }
    })

    return exportText
  }

  const exportData = generateExportFormat(data)

  // Determine the extension based on customer head id
  let extension = '.hlw' // Default
  if (data.length > 0) {
    const customerHeadId = data[0].customerHeadId
    if (customerHeadId === 1000178) {
      extension = '.HLW'
    } else if (customerHeadId === 1000180 || customerHeadId === 1000218) {
      extension = '.txt'
    }
  }
  downloadTextFile(exportData, `salary-deduction-usage-huldt-og-lillevik${extension}`)
}

export function ssdSapFormat(
  data: SalaryDeductionDeviceReport[],
  userCurrency: string | undefined
) {
  let exportText = ''

  data.forEach(item => {
    const employeeNo = item.employeeNo
    const orderDate = dayjs(item.orderDate).format('YYYYMMDD')
    const currency = userCurrency || 'NOK'

    // Regular salary deduction line
    const regularLine = [
      employeeNo,
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '6044',
      orderDate,
      item.fixedSalaryDeductionAmount ?? item.contentServiceAmount ?? '',
      '',
      currency,
    ].join('\t')

    exportText += regularLine + '\n'

    // Fixed salary deduction line if applicable
    if (parseFloat(String(item.fixedSalaryDeductionAmount)) > 0) {
      const fixedLine = [
        employeeNo,
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '6045',
        orderDate,
        item.fixedSalaryDeductionAmount,
        '',
        currency,
      ].join('\t')

      exportText += fixedLine + '\n'
    }

    // Content service line if applicable
    if (parseFloat(String(item.contentServiceAmount)) > 0) {
      const contentLine = [
        employeeNo,
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '7060',
        orderDate,
        item.contentServiceAmount,
        '',
        currency,
      ].join('\t')

      exportText += contentLine + '\n'
    }
  })

  downloadTextFile(exportText, `salary-deduction-device-sap.txt`)
}

export function ssuSapFormat(data: SalaryDeductionUsageReport[]) {
  let exportText = ''

  // Header line
  const header = [
    'Ansattnr',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '6045',
    'Dato',
    'Beløp',
    '',
    'Valuta',
  ].join('\t')
  exportText += header + '\n'

  data.forEach(item => {
    const employeeNo = item.employeeNo
    const accountingCode = item.accountingCode || '6045'
    const orderDate = dayjs(item.orderDate).format('YYYYMMDD') ?? ''
    const currency = item.currency || 'NOK'
    const amount = item.amount || '0.00' // Default to '0.00' if not provided

    const line = [
      employeeNo,
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      accountingCode,
      orderDate,
      amount,
      '',
      currency,
    ].join('\t')

    exportText += line + '\n'
  })
  downloadTextFile(exportText, `salary-deduction-usage-sap.txt`)
}

export function ssuHuldtOgLillevikSdvFormat(
  data: SalaryDeductionUsageReport[],
  { fromDate, toDate }: FilterOptionsType
) {
  function generateExportFormat(data: SalaryDeductionUsageReport[]) {
    let exportText = ''

    data.forEach(item => {
      const employeeNo = item.employeeNo
      const accountingCode = item.accountingCode
      const grossAmount =
        parseFloat(String(item.netVat)) > parseInt(String(item.amount))
          ? item.netVat
          : parseInt(String(item.amount))

      if (
        employeeNo &&
        parseFloat(String(item.fixedSalaryDeductionAmount)) + parseFloat(String(item.amount)) !== 0
      ) {
        const line =
          [
            '0',
            ' '.repeat(30), // 30 spaces
            accountingCode.padStart(12, '0'), // cost object code
            ' '.repeat(60), // 60 spaces
            ' '.repeat(13), // 13 spaces
            ' '.repeat(13), // 13 spaces
            ` ${formatNumber(grossAmount).padStart(11, '0')}`, // one leading space for gross amount
          ].join(';') + ';\n'

        exportText += line
      }
    })

    return exportText
  }

  // Call the generateExportFormat function with your 'data' array
  const exportData = generateExportFormat(data)

  // Construct the header line
  const headerLine =
    [
      '01500000',
      'TELENOR AS' + ' '.repeat(50),
      'TELENOR AS' + ' '.repeat(50),
      '55' + ' '.repeat(55),
      data[0].invoiceNo,
      dayjs(fromDate).format('YYYYMMDD'),
      dayjs(toDate).format('YYYYMMDD'),
      dayjs(data[0].invoiceDate).format('YYYYMMDD'), // Replace with actual invoice date
    ].join(';') + ';\n'

  // Complete the export data by combining the header and the generated export text
  const completeExportData = headerLine + exportData
  downloadTextFile(completeExportData, 'salary-deduction-usage-huldt-og-lillevik.sdv')
}

export function ssdHuldtOgLillevikSdvFormat(
  data: SalaryDeductionDeviceReport[],
  { fromDate, toDate }: FilterOptionsType
) {
  // Function to generate the export format
  function generateExportFormat(data: SalaryDeductionDeviceReport[]) {
    let exportText = ''

    data.forEach(item => {
      const employeeNo = item.employeeNo
      const accountingCode = item.accountingCode
      const grossAmount = (item.netVat || 0) > item.amount ? item.netVat : item.amount

      if (
        employeeNo &&
        parseFloat(String(item.fixedSalaryDeductionAmount)) + parseFloat(String(item.amount)) !== 0
      ) {
        const line =
          [
            '0',
            '                              ', // 30 spaces
            accountingCode.padStart(12, '0'), // cost object code
            '                                                            ', // 60 spaces
            '             ', // 13 spaces
            '             ', // 13 spaces
            ` ${formatNumber(parseFloat(String(grossAmount))).padStart(11, '0')}`, // one leading space for amount
          ].join(';') + ';\n'

        exportText += line
      }
    })

    return exportText
  }
  // Call the generateExportFormat function with your 'data' array
  const exportData = generateExportFormat(data)

  // Construct the header line
  const currentDate = dayjs().format('YYYYMMDD')
  const headerLine =
    [
      '01500000',
      'TCM AS' + ' '.repeat(50),
      'TCM AS' + ' '.repeat(50),
      'MOBILE' + ' '.repeat(55),
      `TCM-DEVICE-${currentDate}`,
      fromDate,
      toDate,
      fromDate,
    ].join(';') + ';\n'

  // Complete the export data by combining the header and the generated export text
  const completeExportData = headerLine + exportData
  downloadTextFile(completeExportData, 'salary-deduction-device-huldt-og-lillevik.sdv')
}

export function ssuVismaLonnV1Format(data: SalaryDeductionUsageReport[]) {
  function generateExportFormat(data: SalaryDeductionUsageReport[]) {
    let exportText = ''

    data.forEach(item => {
      const employeeNo = item.employeeNo
      const salaryDeductionCode = item.accountingCode
      const grossAmount =
        parseFloat(String(item.netVat)) > parseFloat(String(item.amount))
          ? item.netVat
          : item.amount

      if (
        employeeNo &&
        parseFloat(String(item.fixedSalaryDeductionAmount)) + parseFloat(String(item.amount)) !== 0
      ) {
        const line =
          [
            dayjs().format('YYYY'),
            `TCM${dayjs().format('YYYYMMDD')}`,
            employeeNo,
            salaryDeductionCode,
            '',
            formatNumber(grossAmount),
            '',
            '',
            '',
            '',
            ' ',
            ' ',
            ' ',
            ' ',
            ' ',
            ' ',
          ].join(';') + '\n'

        exportText += line
      }
    })

    return exportText
  }

  const exportData = generateExportFormat(data)
  downloadTextFile(exportData, 'salary-deduction-usage-visma-lonn-v1.csv')
}

export function ssdVismaLonnV1Format(data: SalaryDeductionDeviceReport[]) {
  function generateExportFormat(data: SalaryDeductionDeviceReport[]) {
    let exportText = ''

    data.forEach(item => {
      const line =
        [
          dayjs().format('YYYY'),
          `TCM${dayjs().format('YYYYMMDD')}`,
          item.employeeNo ?? '',
          item.accountingCode ?? '',
          '',
          formatNumber(item.amount),
          '',
          '',
          '',
          '',
          ' ',
          ' ',
          ' ',
          ' ',
          ' ',
          ' ',
        ].join(';') + '\n'

      exportText += line
    })

    return exportText
  }

  const exportData = generateExportFormat(data)
  downloadTextFile(exportData, 'salary-deduction-device-visma-lonn-v1.csv')
}

export function ssuVismaLonnV2Format(data: SalaryDeductionUsageReport[], isVismaPayroll = false) {
  function generateExportFormat(data: SalaryDeductionUsageReport[]) {
    let exportText = ''

    data.forEach(item => {
      const employeeNo = item.employeeNo
      const salaryDeductionCode = item.accountingCode
      const netVatAmount = parseFloat(String(item.netVat))
      const isCustomerHeadId = item.customerHeadId !== 1000192

      if (
        employeeNo &&
        item.dim3 !== '2060' &&
        parseFloat(String(item.fixedSalaryDeductionAmount)) + netVatAmount !== 0
      ) {
        const line =
          [
            isCustomerHeadId ? 0 : '',
            isCustomerHeadId ? 0 : '',
            employeeNo,
            salaryDeductionCode,
            isCustomerHeadId ? 0 : '',
            isCustomerHeadId ? 0 : '',
            isCustomerHeadId ? 0 : '',
            isVismaPayroll
              ? -netVatAmount
              : formatVismaPayrollNumber(-netVatAmount, isVismaPayroll),
            isCustomerHeadId ? 0 : '',
            isCustomerHeadId ? 0 : '',
            ' ',
            ' ',
            ' ',
            ' ',
            ' ',
            ' ',
          ].join(';') + '\n'

        exportText += line
      }
    })

    return exportText
  }

  const exportData = generateExportFormat(data)
  downloadTextFile(exportData, 'salary-deduction-usage-visma-lonn-v2.csv')
}

export function ssdVismaLonnV2Format(data: SalaryDeductionDeviceReport[], isVismaPayroll = false) {
  function generateExportFormat(data: SalaryDeductionDeviceReport[]) {
    let exportText = ''

    data.forEach(item => {
      const employeeNo = item.employeeNo
      const amount = parseFloat(String(item.amount))
      const isCustomerHeadId = item.customerHeadId !== 1000192

      if (employeeNo && amount !== 0) {
        const line =
          [
            isCustomerHeadId ? '' : 0,
            isCustomerHeadId ? '' : 0,
            employeeNo ?? '',
            item.accountingCode ?? '',
            isCustomerHeadId ? '' : 0,
            isCustomerHeadId ? '' : 0,
            isCustomerHeadId ? '' : 0,
            isVismaPayroll ? -amount : formatVismaPayrollNumber(-amount, isVismaPayroll),
            isCustomerHeadId ? '' : 0,
            item.dim1 ?? '0',
            ' ',
            ' ',
            ' ',
            ' ',
            ' ',
            ' ',
          ].join(';') + '\n'

        exportText += line
      }
    })

    return exportText
  }

  const exportData = generateExportFormat(data)
  downloadTextFile(exportData, 'salary-deduction-device-visma-lonn-v2.csv')
}

export function ssuVismaLonnV3Format(data: SalaryDeductionUsageReport[]) {
  let exportText = ''

  data.forEach(item => {
    const employeeNo = item.employeeNo
    const netVatAmount = parseFloat(String(item.netVat))

    if (
      employeeNo &&
      item.dim3 !== '2060' &&
      parseFloat(String(item.fixedSalaryDeductionAmount)) + netVatAmount !== 0
    ) {
      const line =
        [
          '',
          '',
          employeeNo,
          item.accountingCode,
          '',
          '',
          '',
          formatNumber(netVatAmount),
          '',
          '',
          ' ',
          ' ',
          ' ',
          ' ',
          ' ',
          ' ',
        ].join(';') + '\n'

      exportText += line
    }
  })
  downloadTextFile(exportText, 'salary-deduction-usage-visma-lonn-v3.csv')
}

export function ssdVismaLonnV3Format(data: SalaryDeductionDeviceReport[]) {
  let exportText = ''

  data.forEach(item => {
    const line =
      [
        '',
        '',
        item.employeeNo ?? '',
        item.accountingCode ?? '',
        '',
        '',
        '',
        formatVismaPayrollNumber(-item.amount, true),
        '',
        '',
        ' ',
        ' ',
        ' ',
        ' ',
        ' ',
        ' ',
      ].join(';') + '\n'

    exportText += line
  })
  downloadTextFile(exportText, 'salary-deduction-device-visma-lonn-v3.csv')
}

export const getReportExportUsingAPI = (
  filters: Pick<ExportFiltersType, 'reportName' | 'formatName'> & Partial<ExportFiltersType>
) => {
  const parameters: ExportFiltersType = {
    reportName: filters.reportName,
    formatName: filters.formatName,
    countryId: 0,
    isGlobal: filters.isGlobal || false,
    fromDate: filters.fromDate || '',
    toDate: filters.toDate || '',
    customerId: filters.customerId,
  }

  adminResources.getBinaryExports(parameters).then(response => {
    let content_type = ''
    response.headers.forEach((value, name) => {
      if (name === 'content-type') {
        content_type = value
      }
    })

    const fileExtension = content_type.substring(content_type.lastIndexOf('/') + 2)
    const blob = new Blob([response.buffer], { type: content_type })
    const filename = `${filters.reportName}-${filters.formatName}-${parameters.fromDate}-${parameters.toDate}.${fileExtension}`

    const anchor = document.createElement('a')
    anchor.href = URL.createObjectURL(blob)
    anchor.download = filename

    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
  })
}
